import { session, local } from 'store2';
import { LoginResponse, LoginStatus } from '@dev/ee-api/ee-api-models';

import { login } from './ElasticApi';

/**
 * @class
 * @classdesc Class used for account authentication
 */
class Authentication {
    /**
     * Method used to check if user is authenticated in system
     * @example
     * Authentication.isAuthenticated();
     * @returns {boolean} returns tue if user is authenticated
     */
    public static isAuthenticated(): boolean {
        return session.has('token') || local.has('token');
    }

    /**
     * Method used to get personal access token which is mandator for sending api calls
     * @throws Will throw error when token is missing in local and session storage
     * @example
     * Authentication.getApiToken();
     * @returns {string} returns string with users access token
     */
    public static getAccessToken(): string {
        if (!session.has('token') && !local.has('token')) {
            throw new Error('Access token is missing!');
        }

        if (session.has('token')) {
            return session.get('token');
        }

        return local.get('token');
    }

    /**
     * Method used for setting access token
     * @param {string} token Personal access token
     * @param {boolean} rememberMe If user is admin you can use session storage
     * to be sure that key will be deleted after session ends
     */
    public static setAccessToken(token: string, rememberMe?: boolean): void {
        if (!rememberMe) {
            session.set('token', token);
            return;
        }

        local.set('token', token);
    }

    /**
     * Method used for deleting account session
     */
    public static removeAccessToken(sessionStorageOnly = false): void {
        if (local.has('token') && !sessionStorageOnly) {
            local.remove('token');
        }

        if (session.has('token')) {
            session.remove('token');
        }
        if (local.has('key') && !sessionStorageOnly) {
            local.remove('key');
        }

        if (session.has('key')) {
            session.remove('key');
        }
    }

    /**
     * Method used for account authentication
     * @param email account email
     * @param password account password
     * @param twoFactorCode if account has 2fa enabled, you can pass 2fa code here
     * @param boolean use session or local storage
     */
    public static authenticate(email: string, password: string, twoFactorCode?: string, rememberMe = false): Promise<LoginStatus> {
        const params: { UserName: string; Password: string; TwoFactorAuthCode?: string } = {
            UserName: email,
            Password: password,
        };

        if (twoFactorCode) {
            params.TwoFactorAuthCode = twoFactorCode;
        }

        return login(params).then((resp: LoginResponse): LoginStatus => {
            if (resp && resp.ApiKey) {
                Authentication.setAccessToken(resp.ApiKey, rememberMe);
            }

            return resp.Status;
        });
    }

    /**
     * Checks if account has access level
     * @param accessLevel
     */
    public static hasAccessLevel(accessLevel: string): boolean {
        console.log({ accessLevel });
        return true;
    }
}

export default Authentication;
